import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Add, DeleteOutline, Edit } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

const useStyles = makeStyles(theme => ({
  headerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  formBox: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    background: "#fafafa",
  },
  appointmentRow: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
  },
  rowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  rowActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  emptyText: {
    color: theme.palette.text.secondary,
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
}));

const pad = value => String(value).padStart(2, "0");

const toDateTimeInput = value => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const statusLabels = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  rescheduled: "Reprogramada",
  completed: "Completada",
  no_show: "No asistio",
};

const emptyForm = {
  id: null,
  serviceId: "",
  startsAt: "",
  status: "confirmed",
  notes: "",
};

const TicketAgendaModal = ({ modalOpen, onClose, ticket }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [deletingReservation, setDeletingReservation] = useState(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const resetForm = () => {
    const nextDate = new Date();
    nextDate.setMinutes(nextDate.getMinutes() + 30);
    setForm({
      ...emptyForm,
      serviceId: services[0]?.id || "",
      startsAt: toDateTimeInput(nextDate),
    });
  };

  const fetchData = async () => {
    if (!modalOpen || !ticket?.id) return;

    setLoading(true);
    try {
      const start = new Date();
      start.setFullYear(start.getFullYear() - 1);
      const end = new Date();
      end.setFullYear(end.getFullYear() + 1);
      const [{ data: serviceData }, { data: reservationData }] = await Promise.all([
        api.get("/appointments/services"),
        api.get("/appointments/reservations", {
          params: {
            start: start.toISOString(),
            end: end.toISOString(),
            contactId: ticket.contactId || ticket.contact?.id,
            connectionId: ticket.connectionId,
          },
        }),
      ]);

      const availableServices = (serviceData.services || []).filter(
        service => String(service.connectionId || "") === String(ticket.connectionId || "")
      );

      if (isMounted.current) {
        setServices(availableServices);
        setReservations(reservationData.reservations || []);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen) {
      setShowForm(false);
      fetchData();
    }
  }, [modalOpen, ticket]);

  useEffect(() => {
    if (modalOpen && services.length && !form.serviceId) {
      resetForm();
    }
  }, [services, modalOpen]);

  const handleEdit = reservation => {
    setForm({
      id: reservation.id,
      serviceId: reservation.serviceId,
      startsAt: toDateTimeInput(reservation.startsAt),
      status: reservation.status || "confirmed",
      notes: reservation.notes || "",
    });
    setShowForm(true);
  };

  const validateForm = () => {
    if (!form.serviceId || !form.startsAt) {
      toast.error("Selecciona servicio, fecha y hora.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        contactId: ticket.contactId || ticket.contact?.id,
        customerName: ticket.contact?.name || "",
        customerPhone: ticket.contact?.number || "",
        customerEmail: ticket.contact?.email || "",
        connectionId: ticket.connectionId,
        userId: ticket.userId || "",
        source: "manual",
      };

      if (form.id) {
        await api.put(`/appointments/reservations/${form.id}`, payload);
      } else {
        await api.post("/appointments/reservations", payload);
      }

      toast.success("Agenda guardada");
      setShowForm(false);
      fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async reservationId => {
    try {
      await api.delete(`/appointments/reservations/${reservationId}`);
      toast.success("Agenda eliminada");
      setDeletingReservation(null);
      fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const formatDate = value => (value ? new Date(value).toLocaleString() : "");

  return (
    <>
      <Dialog open={modalOpen} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>Agendar</DialogTitle>
        <DialogContent dividers>
          <div className={classes.headerActions}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
            >
              {privacyCensorSettings.contactNames
                ? "Contacto privado"
                : `${ticket?.contact?.name || ""} ${
                    ticket?.contact?.number ? `- ${ticket.contact.number}` : ""
                  }`}
            </Typography>
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              disabled={!services.length}
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                } else {
                  resetForm();
                  setShowForm(true);
                }
              }}
            >
              {showForm ? "Cancelar" : "Agregar"}
            </Button>
          </div>

          {showForm && (
            <Paper className={classes.formBox} elevation={0}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Servicio</InputLabel>
                    <Select
                      value={form.serviceId}
                      label="Servicio"
                      onChange={event => setForm({ ...form, serviceId: event.target.value })}
                    >
                      {services.map(service => (
                        <MenuItem key={service.id} value={service.id}>
                          {service.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Contacto"
                    value={
                      privacyCensorSettings.contactNames
                        ? "Contacto privado"
                        : `${ticket?.contact?.name || ""} - ${ticket?.contact?.number || ""}`
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Conexion"
                    value={ticket?.connection?.name || ticket?.connectionId || ""}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Fecha y hora"
                    type="datetime-local"
                    value={form.startsAt}
                    onChange={event => setForm({ ...form, startsAt: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={form.status}
                      label="Estado"
                      onChange={event => setForm({ ...form, status: event.target.value })}
                    >
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notas"
                    value={form.notes || ""}
                    onChange={event => setForm({ ...form, notes: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={3}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <DialogActions>
                <Button onClick={() => setShowForm(false)} color="secondary" variant="outlined" disabled={saving}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : "Guardar"}
                </Button>
              </DialogActions>
            </Paper>
          )}

          {loading ? (
            <CircularProgress size={24} />
          ) : reservations.length ? (
            reservations.map(reservation => (
              <Paper className={classes.appointmentRow} elevation={0} key={reservation.id}>
                <div className={classes.rowHeader}>
                  <div>
                    <Typography variant="subtitle2">
                      {reservation.service?.name || reservation.title || "Agenda"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatDate(reservation.startsAt)} · {statusLabels[reservation.status] || reservation.status}
                    </Typography>
                    {reservation.reminders?.length ? (
                      <Typography variant="caption" color="textSecondary">
                        {reservation.reminders.length} countdown programado
                        {reservation.reminders.length === 1 ? "" : "s"}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Sin countdowns programados
                      </Typography>
                    )}
                  </div>
                  <div className={classes.rowActions}>
                    <IconButton size="small" onClick={() => handleEdit(reservation)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingReservation(reservation)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" className={classes.emptyText}>
              Este contacto no tiene agendas.
            </Typography>
          )}

          {!services.length && !loading && (
            <Typography variant="body2" color="textSecondary">
              No hay servicios de Agenda activos para esta conexion.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Eliminar agenda"
        open={!!deletingReservation}
        onClose={() => setDeletingReservation(null)}
        onConfirm={() => handleDelete(deletingReservation.id)}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>
    </>
  );
};

export default TicketAgendaModal;
